import { formatCurrency } from '@/utils/currencyUtils';
import { useDateFormatter } from '@acast-tech/decibel';
import { CampaignTypesApiResponse } from '@/types/api/campaign-types';
import { VisibleCategories } from '@/types/modals';

interface CampaignTypeEarningsTooltipContentProps {
  payload: CampaignTypesApiResponse[number];
  visibleCategories: VisibleCategories;
}

/**
 * Content component for chart tooltips displaying value and currency information
 * @param payload - The payload of the tooltip.
 * @param visibleCategories - The visible categories for the campaign types modal.
 */
const CampaignTypeEarningsTooltipContent = ({
  payload,
  visibleCategories,
}: CampaignTypeEarningsTooltipContentProps) => {
  const formatter = useDateFormatter({ month: 'short', day: 'numeric' });

  if (!payload || !payload.totals || !payload.calculation_date) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium capitalize mr-6">
        {formatter.format(new Date(payload.calculation_date))}
      </p>

      {Object.entries(payload.totals).map(
        ([key, value], i) =>
          visibleCategories[key as keyof typeof visibleCategories].visible && (
            <p className="text-xs inline-flex items-center gap-1" key={`${i}-${value.category}`}>
              <span
                className={`w-3 h-3 rounded-full`}
                style={{
                  backgroundColor: visibleCategories[key as keyof typeof visibleCategories].color,
                }}
              />
              <span>{value.category}:</span>
              <strong>
                {formatCurrency(value.total?.amount || 0, value.total?.currency, true)}
              </strong>
            </p>
          )
      )}
    </div>
  );
};

export default CampaignTypeEarningsTooltipContent;
